import { Alert, AlertTitle, Box, Button, Stack, TextField, Typography } from '@mui/material'
import { useState } from 'preact/hooks'
import { api } from '../api/client'
import type { HelpMessage } from '../api/generated/HelpMessage'
import type { HelpRequest } from '../api/generated/HelpRequest'
import { useAction } from '../api/useAction'
import { useEvent } from '../events/EventContext'
import { requestWho, respondersLine } from './format'
import { useHelp } from './HelpContext'

const messages = {
  not_found: "Cette demande d'aide est close.",
  forbidden: "Vous n'êtes plus en poste pour répondre.",
}

/** The user's own request: who comes, a message to add, and « C'est réglé ». */
function OwnRequest({ request }: { request: HelpRequest }) {
  const { event } = useEvent()
  const help = useHelp()
  const [message, setMessage] = useState(request.message)
  const { error, pending, run } = useAction(messages)
  const path = `/events/${event.slug}/help/${request.id}`
  const send = (e: Event) => {
    e.preventDefault()
    run(async () => {
      const body: HelpMessage = { message }
      await api.patch(path, body)
      help.reload()
    })
  }
  const close = () =>
    run(async () => {
      await api.post(`${path}/close`, {})
      help.reload()
    })
  return (
    <Alert severity="error" variant="filled" icon={false}>
      <AlertTitle sx={{ fontWeight: 700 }}>Alerte envoyée</AlertTitle>
      <Typography variant="body2" sx={{ mb: 1.5 }}>
        {respondersLine(request.responders) ??
          'Les organisateurs et les bénévoles de renfort en poste sont prévenus.'}
      </Typography>
      <Stack component="form" direction="row" spacing={1} onSubmit={send} sx={{ mb: 1.5 }}>
        <TextField
          size="small"
          placeholder="Ajouter un message (facultatif)"
          value={message}
          onInput={(e) => setMessage((e.target as HTMLInputElement).value)}
          slotProps={{ htmlInput: { maxLength: 300 } }}
          sx={{ flex: 1, bgcolor: 'background.paper', borderRadius: 1 }}
        />
        <Button
          type="submit"
          variant="contained"
          color="inherit"
          disabled={pending || message.trim() === request.message}
          sx={{ color: 'error.main' }}
        >
          Envoyer
        </Button>
      </Stack>
      <Button variant="outlined" color="inherit" disabled={pending} onClick={close}>
        C'est réglé
      </Button>
      {error && <Typography variant="body2">{error}</Typography>}
    </Alert>
  )
}

/** Someone else's request, for a responder: « J'y vais », and closing for an organizer. */
function OthersRequest({ request }: { request: HelpRequest }) {
  const { event } = useEvent()
  const help = useHelp()
  const { error, pending, run } = useAction(messages)
  const path = `/events/${event.slug}/help/${request.id}`
  const post = (action: 'going' | 'close') =>
    run(async () => {
      await api.post(`${path}/${action}`, {})
      help.reload()
    })
  const coming = respondersLine(request.responders)
  return (
    <Alert severity="error" variant="filled" icon={false}>
      <AlertTitle sx={{ fontWeight: 700 }}>Besoin d'aide : {requestWho(request)}</AlertTitle>
      {request.message && <Typography sx={{ mb: 1 }}>« {request.message} »</Typography>}
      <Typography variant="body2" sx={{ mb: 1.5 }}>
        {coming ?? "Personne n'a encore répondu."}
      </Typography>
      <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1 }}>
        {!request.going && (
          <Button
            variant="contained"
            color="inherit"
            disabled={pending}
            onClick={() => post('going')}
            sx={{ color: 'error.main' }}
          >
            J'y vais
          </Button>
        )}
        {event.role === 'organizer' && (
          <Button
            variant="outlined"
            color="inherit"
            disabled={pending}
            onClick={() => post('close')}
          >
            Clore
          </Button>
        )}
      </Stack>
      {error && <Typography variant="body2">{error}</Typography>}
    </Alert>
  )
}

/** Open help requests, on top of every page of the event space. */
export function HelpBanner() {
  const { data } = useHelp()
  if (!data?.requests.length) return null
  return (
    <Box
      role="alert"
      sx={{ px: { xs: 2, md: 4 }, pt: 2, maxWidth: 'md', mx: 'auto', displayPrint: 'none' }}
    >
      <Stack spacing={1.5}>
        {data.requests.map((request) =>
          request.mine ? (
            <OwnRequest key={request.id} request={request} />
          ) : (
            <OthersRequest key={request.id} request={request} />
          ),
        )}
      </Stack>
    </Box>
  )
}
